import {
  PdfGen,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PageGen,
  PdfFont,
  PdfUnit,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { GCalc } from '../../pdf/gcalc';
import { calcOrigin } from '../../core/genHelper';
import { urlQr } from '../../core/urlQr';
import { HintUtil } from '../../core/hintUtil';
import { IItemCount, SITE_URL } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/barcode';
import '../../pdf/draw/arrow';
import '../../pdf/draw/shapes';
import '../../pdf/draw/helpers';

const BLUE = new PdfBrush(PdfColor.fromRgb(0, 0, 255));
const BLUE_COLOR = PdfColor.fromRgb(0, 0, 255);

function removeDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function genId(rnd: { next(a: number, b: number): number }): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 4 }, () => chars[rnd.next(0, chars.length)]).join('');
}

/** 4-cell horizontal row layout helper */
function buildFourCells(rect: PdfRect, margin2: number): PdfRect[] {
  const sz = Math.min(rect.width / 4, rect.height);
  const origX = rect.x + (rect.width - 4 * sz) / 2;
  const origY = rect.y + (rect.height - sz) / 2;
  const rs: PdfRect[] = [];
  for (let x = 0; x < 4; x++) {
    rs.push(
      new PdfRect(origX + x * sz + margin2, origY + margin2, sz - 2 * margin2, sz - 2 * margin2)
    );
  }
  return rs;
}

// ---------------------------------------------------------------------------
// QR_Hra__Hunt
// Multi-page × 5 items; label + counter + answer hunt QR
// ---------------------------------------------------------------------------
export class QR_Hra__Hunt implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 5;
    const total = opts.list.length;
    const stripH = PdfSize.A4.height / countY;
    const id = genId(opts.data.random);
    const totalPages = Math.ceil(total / countY);

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage();
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const labelRects = pageItems.map((_, i) => {
        const rect = new PdfRect(0, i * stripH, PdfSize.A4.width, stripH);
        const textRect = new PdfRect(
          rect.x + margin,
          rect.y + margin,
          rect.width - rect.height - margin,
          rect.height - 2 * margin
        );
        return new PdfRect(
          textRect.x + rect.width * 0.3,
          textRect.y,
          rect.height - 2 * margin,
          rect.height - 2 * margin
        );
      });

      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(labelRects[0].width, labelRects[0].height)
      );

      for (let i = 0; i < pageItems.length; i++) {
        const item = pageItems[i];
        const index = pageStartIndex + i + 1;
        const rect = new PdfRect(0, i * stripH, PdfSize.A4.width, stripH);
        const textRect = new PdfRect(
          rect.x + margin,
          rect.y + margin,
          rect.width - rect.height - margin,
          rect.height - 2 * margin
        );
        const qrRect = new PdfRect(
          rect.right - rect.height + margin,
          rect.y + margin,
          rect.height - 2 * margin,
          rect.height - 2 * margin
        );
        const labelRect = labelRects[i];

        const answer = item.text;
        const rem = removeDiacritics(answer);
        const answers = [...new Set([answer, rem, rem.toLowerCase().trim()])];
        const isNumeric = /^\s*-?\d+(\.\d+)?\s*$/.test(answer);

        const url = urlQr(
          { a: 'a', id, i: index, c: total, t: isNumeric ? 'n' : 't', o: answers },
          SITE_URL
        );

        p.drawRectangle(GUtil.cutPen, null, rect);
        p.drawLabel(item.label as any, { rect: labelRect, font: labelFont });
        p.drawString(
          `#${index}/${total}`,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          textRect,
          ETextAlign.TopLeft
        );
        p.drawQr(qrRect, PdfBrush.Black, url);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Path
// Multi-page × 5 items; answer chain with START/STOP icons and arrow to next
// ---------------------------------------------------------------------------
export class QR_Hra__Path implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin1 = PdfUnit.FromMillimeters(5);
    const margin2 = PdfUnit.FromMillimeters(2);
    const countY = 5;
    const total = opts.list.length;
    const { origin, size } = calcOrigin(1, countY, margin1, PdfSize.A4);
    const id = genId(opts.data.random);
    const totalPages = Math.ceil(total / countY);

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage();
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(size.width / 4, size.height)
      );

      for (let i = 0; i < pageItems.length; i++) {
        const item = pageItems[i];
        const index = pageStartIndex + i + 1;
        const nextItem = opts.list[index % total];
        const rect = new PdfRect(origin.x, origin.y + i * size.height, size.width, size.height);

        const r = new PdfRect(rect.x, rect.y, rect.width * 0.75, rect.height);
        const rs = buildFourCells(rect, margin2);
        const rs0b = new PdfRect(
          rs[0].x,
          rs[0].y + rs[0].height / 2,
          rs[0].width,
          rs[0].height / 2
        );
        const rs1 = new PdfRect(rs[3].x, rs[3].y, rs[3].width, rs[3].height / 2);
        const rs2 = new PdfRect(rs[3].x, rs[3].y + rs[3].height / 2, rs[3].width, rs[3].height / 2);
        const icon = new PdfRect(rs[0].x, rs[0].y, rs[0].height / 5, rs[0].height / 5);

        const data: Record<string, unknown> = { a: 'm', id, i: index, c: total, o: item.text };
        if (index < total) data.o1 = nextItem.text;
        const url = urlQr(data, SITE_URL);

        if (index === 1) p.drawStart(icon, BLUE_COLOR);
        if (index === total) p.drawStop(icon, BLUE_COLOR);

        p.drawString(item.text, GUtil.baseFont, PdfBrush.Black, null, rs0b, ETextAlign.Center);
        p.drawLabel(item.label as any, { rect: rs[1], font: labelFont });
        p.drawQr(rs[2], PdfBrush.Black, url);

        if (index < total) {
          const f = p.adjustFont(rs2.width, [nextItem.text], GUtil.baseFont);
          p.drawArrowRight(rs1, BLUE);
          p.drawString(nextItem.text, f, PdfBrush.Black, null, rs2, ETextAlign.Center);
        }

        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Trail
// Multi-page × 5 items; current answer + next answer trail
// ---------------------------------------------------------------------------
export class QR_Hra__Trail implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin1 = PdfUnit.FromMillimeters(5);
    const margin2 = PdfUnit.FromMillimeters(2);
    const countY = 5;
    const total = opts.list.length;
    const { origin, size } = calcOrigin(1, countY, margin1, PdfSize.A4);
    const id = genId(opts.data.random);
    const totalPages = Math.ceil(total / countY);

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage();
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(size.width / 4, size.height)
      );

      for (let i = 0; i < pageItems.length; i++) {
        const item = pageItems[i];
        const index = pageStartIndex + i + 1;
        const nextItem = opts.list[index % total];
        const rect = new PdfRect(origin.x, origin.y + i * size.height, size.width, size.height);

        const r = new PdfRect(rect.x, rect.y, rect.width * 0.75, rect.height);
        const rs = buildFourCells(rect, margin2);
        const rs1 = new PdfRect(rs[3].x, rs[3].y, rs[3].width, rs[3].height / 2);
        const rs2 = new PdfRect(rs[3].x, rs[3].y + rs[3].height / 2, rs[3].width, rs[3].height / 2);

        const url = urlQr(
          { a: 'e', id, i: index, c: total, o: item.text, o1: nextItem.text },
          SITE_URL
        );

        p.drawString(item.text, GUtil.baseFont, PdfBrush.Black, null, rs[0], ETextAlign.Center);
        p.drawLabel(item.label as any, { rect: rs[1], font: labelFont });
        p.drawQr(rs[2], PdfBrush.Black, url);

        const f = p.adjustFont(rs2.width, [nextItem.text], GUtil.baseFont);
        p.drawArrowRight(rs1, BLUE);
        p.drawString(nextItem.text, f, PdfBrush.Black, null, rs2, ETextAlign.Center);

        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Seek
// Multi-page × 5 items; counter + label + text + seek QR
// ---------------------------------------------------------------------------
export class QR_Hra__Seek implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 5;
    const total = opts.list.length;
    const stripH = PdfSize.A4.height / countY;
    const id = genId(opts.data.random);
    const totalPages = Math.ceil(total / countY);

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage();
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const sz = Math.min(PdfSize.A4.width / 4, stripH);
      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(sz - 2 * margin, sz - 2 * margin)
      );

      for (let i = 0; i < pageItems.length; i++) {
        const item = pageItems[i];
        const index = pageStartIndex + i + 1;
        const rect = new PdfRect(0, i * stripH, PdfSize.A4.width, stripH);

        const szRow = Math.min(rect.width / 4, rect.height);
        const origX = rect.x + (rect.width - 4 * szRow) / 2;
        const origY = rect.y + (rect.height - szRow) / 2;
        const rs: PdfRect[] = [];
        for (let x = 0; x < 4; x++) {
          rs.push(
            new PdfRect(
              origX + x * szRow + margin,
              origY + margin,
              szRow - 2 * margin,
              szRow - 2 * margin
            )
          );
        }

        const url = urlQr({ a: 'f', id, i: index, c: total, o: item.text }, SITE_URL);

        p.drawString(
          `#${index}/${total}`,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          rs[0],
          ETextAlign.TopLeft
        );
        p.drawLabel(item.label as any, { rect: rs[1], font: labelFont });
        p.drawString(item.text, GUtil.baseFont, PdfBrush.Black, null, rs[2], ETextAlign.Center);
        p.drawQr(rs[3], PdfBrush.Black, url);
        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Check
// Multi-page × 5 items; 2 QR per item (correct + wrong)
// ---------------------------------------------------------------------------
export class QR_Hra__Check implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin1 = PdfUnit.FromMillimeters(5);
    const margin2 = PdfUnit.FromMillimeters(2);
    const countY = 5;
    const total = opts.list.length;
    const { origin, size } = calcOrigin(1, countY, margin1, PdfSize.A4);
    const rnd = opts.data.random;
    const id = genId(rnd);
    const totalPages = Math.ceil(total / countY);

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage();
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(size.width / 4, size.height)
      );

      for (let i = 0; i < pageItems.length; i++) {
        const item = pageItems[i];
        const index = pageStartIndex + i + 1;
        const rect = new PdfRect(origin.x, origin.y + i * size.height, size.width, size.height);
        const rs = buildFourCells(rect, margin2);

        const r12 = new PdfRect(rect.x, rect.y, rect.width * 0.5, rect.height);
        const r3 = new PdfRect(rect.x + rect.width * 0.5, rect.y, rect.width * 0.25, rect.height);
        const r4 = new PdfRect(rect.x + rect.width * 0.75, rect.y, rect.width * 0.25, rect.height);

        const rnd1 = `${rnd.next(0, 10)}${rnd.next(0, 10)}${rnd.next(0, 10)}`;
        const rnd2 = `${rnd.next(0, 10)}${rnd.next(0, 10)}${rnd.next(0, 10)}`;
        const url1 = urlQr({ a: 'c', id, o: 0, rnd: rnd1 }, SITE_URL);
        const url2 = urlQr({ a: 'c', id, o: 1, rnd: rnd2 }, SITE_URL);

        p.drawString(item.text, GUtil.baseFont, PdfBrush.Black, null, rs[0], ETextAlign.Center);
        p.drawLabel(item.label as any, { rect: rs[1], font: labelFont });
        p.drawQr(rs[2], PdfBrush.Black, url1);
        p.drawQr(rs[3], PdfBrush.Black, url2);

        p.drawRectangle(GUtil.cutPen, null, r12);
        p.drawRectangle(GUtil.cutPen, null, r3);
        p.drawRectangle(GUtil.cutPen, null, r4);
        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Collect
// Multi-page (1 per item); 2×5 grid of 10 collectible copies per page
// ---------------------------------------------------------------------------
export class QR_Hra__Collect implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const { size } = calcOrigin(2, 5, 0, PdfSize.A4);
    const total = opts.list.length;
    const id = genId(opts.data.random);

    for (let pageIdx = 0; pageIdx < total; pageIdx++) {
      const p = pdf.addPage();
      const item = opts.list[pageIdx];
      let counter = 0;

      const labelFont = p.adjustFontMulti(
        [[item.label.getText() ?? '']],
        GUtil.baseFont,
        new PdfSize(size.width / 2 - 2 * margin, size.height - 2 * margin)
      );

      for (let y = 0; y < 5; y++) {
        for (let x = 0; x < 2; x++) {
          counter++;
          const rect = new PdfRect(x * size.width, y * size.height, size.width, size.height);
          const r = new PdfRect(
            x * size.width + margin,
            y * size.height + margin,
            size.width - 2 * margin,
            size.height - 2 * margin
          );
          const r0 = new PdfRect(r.x, r.y, r.width / 2, r.height);
          const r1 = new PdfRect(r0.right, r.y, r.width / 2, r.height);

          const url = urlQr(
            { a: 'n', id, c: total, o: item.text, i: pageIdx + 1, v: counter },
            SITE_URL
          );
          p.drawQr(r0, PdfBrush.Black, url);
          p.drawLabel(item.label as any, { rect: r1, font: labelFont });
          p.drawRectangle(GUtil.cutPen, null, rect);
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Pairs
// Multi-page; pairs of cards — question/answer sides
// ---------------------------------------------------------------------------
export class QR_Hra__Pairs implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 5;
    const total = opts.list.length;
    const stripH = PdfSize.A4.height / countY;
    const id = genId(opts.data.random);
    const totalPages = Math.ceil(total / countY);

    const pages: PageGen[] = [];
    for (let i = 0; i < totalPages; i++) pages.push(pdf.addPage()); // pages for label+label
    for (let i = 0; i < totalPages; i++) pages.push(pdf.addPage()); // pages for label+text

    for (let page = 0; page < totalPages; page++) {
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const sz = Math.min(PdfSize.A4.width / 4, stripH);
      const labelFont = pages[0].adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(sz - 2 * margin, sz - 2 * margin)
      );

      for (let i = 0; i < pageItems.length; i++) {
        const item = pageItems[i];
        const index = pageStartIndex + i + 1;
        const rect = new PdfRect(0, i * stripH, PdfSize.A4.width, stripH);
        const r1 = new PdfRect(rect.x, rect.y, rect.width / 2, rect.height);
        const r2 = new PdfRect(r1.right, rect.y, rect.width / 2, rect.height);

        const szRow = Math.min(rect.width / 4, rect.height);
        const origX = rect.x + (rect.width - 4 * szRow) / 2;
        const origY = rect.y + (rect.height - szRow) / 2;
        const rs: PdfRect[] = [];
        for (let x = 0; x < 4; x++) {
          rs.push(
            new PdfRect(
              origX + x * szRow + margin,
              origY + margin,
              szRow - 2 * margin,
              szRow - 2 * margin
            )
          );
        }

        const url1 = urlQr({ a: 'd', id, i: index, c: total, g: 2, v: 1 }, SITE_URL);
        const url2 = urlQr({ a: 'd', id, i: index, c: total, g: 2, v: 2 }, SITE_URL);

        // Label+label cards
        pages[page].drawQr(rs[0], PdfBrush.Black, url1);
        pages[page].drawLabel(item.label as any, { rect: rs[1], font: labelFont });
        pages[page].drawLabel(item.label as any, { rect: rs[2], font: labelFont });
        pages[page].drawQr(rs[3], PdfBrush.Black, url2);
        pages[page].drawRectangle(GUtil.cutPen, null, r1);
        pages[page].drawRectangle(GUtil.cutPen, null, r2);

        // Label+text cards
        const pIdx = totalPages + page;
        pages[pIdx].drawQr(rs[0], PdfBrush.Black, url1);
        pages[pIdx].drawLabel(item.label as any, { rect: rs[1], font: labelFont });
        pages[pIdx].drawString(
          item.text,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          rs[2],
          ETextAlign.Center
        );
        pages[pIdx].drawQr(rs[3], PdfBrush.Black, url2);
        pages[pIdx].drawRectangle(GUtil.cutPen, null, r1);
        pages[pIdx].drawRectangle(GUtil.cutPen, null, r2);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Quiz
// Multi-page × 2 items; 3-choice quiz with scored QR codes
// ---------------------------------------------------------------------------
export class QR_Hra__Quiz implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin1 = PdfUnit.FromMillimeters(5);
    const labelHeight = PdfUnit.FromMillimeters(10);
    const countY = 2;
    const choiceCount = 3;
    const total = opts.list.length;
    const stripH = PdfSize.A4.height / countY;
    const rnd = opts.data.random;
    const id = genId(rnd);
    const totalPages = Math.ceil(total / countY);

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage();
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(PdfSize.A4.width - 2 * margin1, stripH / 2 - margin1)
      );

      for (let y = 0; y < pageItems.length; y++) {
        const item = pageItems[y];
        const index = pageStartIndex + y;
        const rect = new PdfRect(0, y * stripH, PdfSize.A4.width, stripH);
        const rm = new PdfRect(
          rect.x + margin1,
          rect.y + margin1,
          rect.width - 2 * margin1,
          rect.height - 2 * margin1
        );
        const r1 = new PdfRect(rm.x, rm.y, rm.width, rm.height / 2);
        const r2 = new PdfRect(rm.x, r1.bottom, rm.width, labelHeight);
        const r3 = new PdfRect(rm.x, r2.bottom, rm.width, r1.height - labelHeight);

        const others = opts.list.filter((_, idx) => idx !== index);
        const shuffledOthers = others.slice().sort(() => rnd.nextDouble() - 0.5);
        const choices = [...shuffledOthers.slice(0, choiceCount - 1), item].sort(
          () => rnd.nextDouble() - 0.5
        );

        const f = p.adjustFont(
          r3.width / choiceCount,
          choices.map((c) => c.text),
          GUtil.baseFont
        );

        p.drawLabel(item.label as any, { rect: r1, font: labelFont });

        for (let j = 0; j < choiceCount; j++) {
          const rx2 = new PdfRect(
            r2.x + (j * r2.width) / choiceCount,
            r2.y,
            r2.width / choiceCount,
            r2.height
          );
          const rx3 = new PdfRect(
            r3.x + (j * r3.width) / choiceCount,
            r3.y,
            r3.width / choiceCount,
            r3.height
          );
          const rq = GCalc.centerRect(rx3);
          const isCorrect = choices[j] === item;
          const url = urlQr(
            { a: 'i', id, i: index + 1, c: total, v: j + 1, p: isCorrect ? 10 : -2 },
            SITE_URL
          );

          p.drawString(choices[j].text, f, PdfBrush.Black, null, rx2, ETextAlign.Center);
          p.drawQr(rq, PdfBrush.Black, url);
          p.drawRectangle(
            GUtil.cutPen,
            null,
            new PdfRect(rx2.x, rx2.y, rx2.width, rx3.bottom - rx2.y)
          );
        }

        p.drawString(
          `#${index + 1}/${total}`,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          rm,
          ETextAlign.TopLeft
        );
        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Task
// 2 pages × 5 items; cycling task types (free/text/number/yesno/multiple)
// ---------------------------------------------------------------------------
function getTaskData(
  id: string,
  index: number,
  total: number,
  rnd: { next: (a: number, b: number) => number; nextDouble: () => number }
): object {
  const t = (index - 1) % 5;

  if (t === 0) return { a: 'g', id, i: index, c: total, t: 'f' };

  if (t === 1) {
    const questions = [
      'Jaký je hlavní objekt na obrázku?',
      'Popiš, co vidíš, jedním slovem.',
      'Jak bys nazval/a tuto věc?',
    ];
    const o = questions[rnd.next(0, questions.length)];
    return { a: 'g', id, i: index, c: total, t: 't', o };
  }

  if (t === 2) {
    const questions = [
      'Odhadni výšku v metrech.',
      'Kolik barev převažuje?',
      'Kolik let je tomuto objektu?',
      'Odhadni váhu v kilogramech.',
    ];
    const o = questions[rnd.next(0, questions.length)];
    return { a: 'g', id, i: index, c: total, t: 'n', o };
  }

  if (t === 3) {
    const questions = [
      'Je to přírodní?',
      'Může se to pohybovat?',
      'Je to organické?',
      'Má to praktickou funkci?',
      'Dá se to použít v kuchyni?',
    ];
    const o = questions[rnd.next(0, questions.length)];
    return { a: 'g', id, i: index, c: total, t: 'b', o };
  }

  // t === 4: multiple choice
  const r = 1 + rnd.next(0, 7);
  const variants: Record<number, object> = {
    1: {
      a: 'g',
      id,
      i: index,
      c: total,
      t: 'm',
      o: 'Do které skupiny tento objekt patří?',
      m: ['věc', 'zvíře', 'rostlina', 'jiné'],
    },
    2: {
      a: 'g',
      id,
      i: index,
      c: total,
      t: 'm',
      o: 'Jaký má objekt povrch?',
      m: ['hladký', 'drsný', 'jiný', ''],
    },
    3: {
      a: 'g',
      id,
      i: index,
      c: total,
      t: 'm',
      o: 'Je objekt přírodní nebo vyrobený člověkem?',
      m: ['přírodní', 'umělý', 'jiný'],
    },
    4: {
      a: 'g',
      id,
      i: index,
      c: total,
      t: 'm',
      o: 'Jak je objekt velký?',
      m: ['malý', 'střední', 'velký', 'jiný'],
    },
    5: {
      a: 'g',
      id,
      i: index,
      c: total,
      t: 'm',
      o: 'Jak moc je objekt složitý?',
      m: ['jednoduchý', 'středně složitý', 'složitý'],
    },
    6: {
      a: 'g',
      id,
      i: index,
      c: total,
      t: 'm',
      o: 'Dokáže se objekt pohybovat?',
      m: ['statický', 'pohyblivý', 'interaktivní'],
    },
    7: {
      a: 'g',
      id,
      i: index,
      c: total,
      t: 'm',
      o: 'Jaké pocity v tobě obrázek vyvolává?',
      m: ['veselý', 'smutný', 'neutrální'],
    },
  };
  return variants[r] ?? variants[1];
}

export class QR_Hra__Task implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 5;
    const total = opts.list.length;
    const stripH = PdfSize.A4.height / countY;
    const rnd = opts.data.random;
    const id = genId(rnd);
    const totalPages = Math.ceil(total / countY);

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage();
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      const sz = Math.min(PdfSize.A4.width / 4, stripH);
      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(sz - 2 * margin, sz - 2 * margin)
      );

      for (let i = 0; i < pageItems.length; i++) {
        const item = pageItems[i];
        const index = pageStartIndex + i + 1;
        const rect = new PdfRect(0, i * stripH, PdfSize.A4.width, stripH);

        const szRow = Math.min(rect.width / 4, rect.height);
        const origX = rect.x + (rect.width - 4 * szRow) / 2;
        const origY = rect.y + (rect.height - szRow) / 2;
        const rs: PdfRect[] = [];
        for (let x = 0; x < 4; x++) {
          rs.push(
            new PdfRect(
              origX + x * szRow + margin,
              origY + margin,
              szRow - 2 * margin,
              szRow - 2 * margin
            )
          );
        }

        const data = getTaskData(id, index, total, rnd);
        const url = urlQr(data, SITE_URL);

        p.drawString(
          `#${index}/${total}`,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          rs[0],
          ETextAlign.TopLeft
        );
        p.drawString(item.text, GUtil.baseFont, PdfBrush.Black, null, rs[1], ETextAlign.Center);
        p.drawLabel(item.label as any, { rect: rs[2], font: labelFont });
        p.drawQr(rs[3], PdfBrush.Black, url);
        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Run
// 5 landscape pages × 2 items; dynamic scoring game with two operation choices
// ---------------------------------------------------------------------------
function roundScore(score: number): number {
  if (score <= 0) return 0;
  const log = Math.max(Math.floor(Math.log10(score)) - 1, 0);
  const pow = 10**log;
  return Math.round(score / pow) * pow;
}

function calcRunOp(a: number, op: string): number {
  let text = op.trim();
  const isPercent = text.endsWith('%');
  if (isPercent) text = text.slice(0, -1).trim();
  const num = parseFloat(text.substring(1).trim());
  const opChar = text[0];
  if (opChar === '+') return isPercent ? a + Math.floor((a * num) / 100) : Math.floor(a + num);
  if (opChar === '-' || opChar === '−')
    return isPercent ? a - Math.floor((a * num) / 100) : Math.floor(a - num);
  if (opChar === '×' || opChar === '*') return Math.floor(a * num);
  if (opChar === ':' || opChar === '÷' || opChar === '/') return Math.floor(a / num);
  return a;
}

function genRunOptions(
  score: number,
  index: number,
  rnd: { next: (a: number, b: number) => number; nextDouble: () => number }
): [string, string] {
  const r = rnd.next(0, 100);
  const list: string[] = [];

  const shuffle = (arr: string[]) => {
    if (rnd.next(0, 2) === 0) [arr[0], arr[1]] = [arr[1], arr[0]];
    return arr;
  };

  if (r < 60 && score < 100) {
    const times = 3 + rnd.next(0, 3);
    const plus1 = score * times;
    const plus2 = Math.floor(plus1 * (rnd.nextDouble() - 0.5) * 0.8);
    list.push(`× ${times}`);
    list.push(`+ ${roundScore(plus1 + plus2)}`);
    return shuffle(list) as [string, string];
  }

  if (r < 40 || index < 2) {
    const plus1 = score;
    const plus2 = Math.floor(score * (rnd.nextDouble() - 0.5) * 0.4);
    list.push('× 2');
    list.push(`+ ${roundScore(plus1 + plus2)}`);
    return shuffle(list) as [string, string];
  }

  if (r < 65) {
    const percent = 10 * (1 + rnd.next(0, 10));
    const plus1 = Math.floor((score * percent) / 100);
    const plus2 = Math.floor(plus1 * (rnd.nextDouble() - 0.5) * 0.7) + rnd.next(0, 11) - 5;
    list.push(`+ ${percent} %`);
    list.push(`+ ${roundScore(plus1 + plus2)}`);
    return shuffle(list) as [string, string];
  }

  if (r < 80) {
    const plus1 = score;
    const plus2 = Math.floor((score / 2) * (rnd.nextDouble() - 0.5) * 0.4);
    list.push(': 2');
    list.push(`− ${roundScore(plus1 + plus2)}`);
    return shuffle(list) as [string, string];
  }

  if (r < 90) {
    const percent = 10 * (1 + rnd.next(0, 3));
    const plus1 = Math.floor((score * percent) / 100);
    const plus2 = Math.floor(plus1 * (rnd.nextDouble() - 0.5) * 0.4) + rnd.next(0, 11) - 5;
    list.push(`− ${percent} %`);
    list.push(`− ${roundScore(plus1 + plus2)}`);
    return shuffle(list) as [string, string];
  }

  list.push(`+ ${1 + roundScore(rnd.next(0, Math.max(1, Math.floor(score / 4))))}`);
  list.push(`− ${1 + roundScore(rnd.next(0, Math.max(1, Math.floor(score / 5))))}`);
  return list as [string, string];
}

function drawRunOp(p: PageGen, op: string, rect: PdfRect): void {
  const f = new PdfFont(PdfFont.FONT_NAME, 42);
  const ch = op.trim()[0];
  const color =
    ch === '+'
      ? PdfColor.fromRgb(0, 0, 255)
      : ch === '-' || ch === '−'
        ? PdfColor.fromRgb(255, 0, 0)
        : ch === '*' || ch === '×'
          ? PdfColor.fromRgb(0, 128, 0)
          : PdfColor.fromRgb(255, 165, 0);
  p.drawString(op, f, new PdfBrush(color), null, rect, ETextAlign.Center);
}

export class QR_Hra__Run implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 2;
    const total = opts.list.length;
    const stripW = PdfSize.A4H.width;
    const stripH = PdfSize.A4H.height / countY;
    const rnd = opts.data.random;
    const id = genId(rnd);
    const totalPages = Math.ceil(total / countY);

    let score = 15;
    const game: [string, string][] = [];
    for (let i = 0; i < total; i++) {
      const options = genRunOptions(score, i, rnd);
      const scores = options.map((op) => calcRunOp(score, op));
      score = Math.max(...scores);
      game.push(options);
    }

    for (let page = 0; page < totalPages; page++) {
      const p = pdf.addPage(true); // landscape
      const pageStartIndex = page * countY;
      const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

      // Calculate label font
      const h2 = stripH / 2;
      const w2 = (stripW * 0.4) / 2 - 2 * margin;
      const labelFont = p.adjustFontMulti(
        pageItems.map((it) => [it.label.getText() ?? '']),
        GUtil.baseFont,
        new PdfSize(w2, h2)
      );

      for (let y = 0; y < pageItems.length; y++) {
        const index = pageStartIndex + y;
        const item = pageItems[y];
        const rect = new PdfRect(0, y * stripH, stripW, stripH);
        const rm = new PdfRect(
          rect.x + margin,
          rect.y + margin,
          rect.width - 2 * margin,
          rect.height - 2 * margin
        );

        const w1 = (rm.width * 0.6) / 2;
        const w2Cell = (rm.width * 0.4) / 2 - margin;
        const h1 = rm.height / 4;
        const h2Cell = rm.height / 2;

        const r1 = new PdfRect(rm.x, rm.y, w1, rm.height);
        const r1a = new PdfRect(r1.right, rm.y, w2Cell, h1);
        const r1b = new PdfRect(r1a.x, r1a.bottom, w2Cell, h2Cell);
        const r1c = new PdfRect(r1b.x, r1b.bottom, w2Cell, h1);

        const r2 = new PdfRect(rm.right - w1, rm.y, w1, rm.height);
        const r2a = new PdfRect(r2.x - w2Cell, r2.y, w2Cell, h1);
        const r2b = new PdfRect(r2a.x, r2a.bottom, w2Cell, h2Cell);
        const r2c = new PdfRect(r2b.x, r2b.bottom, w2Cell, h1);

        const label = `#${index + 1}/${total}`;
        p.drawString(label, GUtil.baseFont, PdfBrush.Black, null, r1a, ETextAlign.Center);
        p.drawString(label, GUtil.baseFont, PdfBrush.Black, null, r2a, ETextAlign.Center);

        p.drawLabel(item.label as any, { rect: r1b, font: labelFont });
        p.drawLabel(item.label as any, { rect: r2b, font: labelFont });

        drawRunOp(p, game[index][0], r1c);
        drawRunOp(p, game[index][1], r2c);

        const url1 = urlQr(
          { a: 'k', id, i: index + 1, c: total, v: 1, o: game[index][0] },
          SITE_URL
        );
        const url2 = urlQr(
          { a: 'k', id, i: index + 1, c: total, v: 2, o: game[index][1] },
          SITE_URL
        );
        p.drawQr(GCalc.centerRect(r1), PdfBrush.Black, url1);
        p.drawQr(GCalc.centerRect(r2), PdfBrush.Black, url2);

        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Hint
// Multi-page (CZ + EN); 3×5 grid of hint QR codes per page
// ---------------------------------------------------------------------------
export class QR_Hra__Hint implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin1 = PdfUnit.FromMillimeters(5);
    const countX = 3,
      countY = 5;
    const big = 2;
    const cellW = PdfSize.A4.width / countX;
    const cellH = PdfSize.A4.height / countY;
    const total = opts.list.length;
    const id = genId(opts.data.random);

    for (let i = 0; i < total; i++) {
      const item = opts.list[i];
      const label = item.text;

      for (let lang = 0; lang < 2; lang++) {
        // 0: CZ, 1: EN
        const p = pdf.addPage();
        const hints = lang === 0 ? HintUtil.getHintsCZ(label) : HintUtil.getHintsEN(label);
        let hintIndex = 0;

        const labelSize = new PdfSize(cellW - 2 * margin1, cellH * big - 2 * margin1);
        const labelFont = p.adjustFontMulti(
          [[item.label.getText() ?? '']],
          GUtil.baseFont,
          labelSize
        );

        for (let y = 0; y < countY; y++) {
          for (let x = 0; x < countX; x++) {
            if (x === 0 && y === 0) {
              const rect = new PdfRect(0, 0, cellW, cellH * big);
              const r = new PdfRect(
                rect.x + margin1,
                rect.y + margin1,
                rect.width - 2 * margin1,
                rect.height - 2 * margin1
              );
              p.drawLabel(item.label as any, { rect: r, font: labelFont });
              p.drawRectangle(GUtil.cutPen, null, rect);
            } else if (x === 0 && y < big) {
              // occupied by large label cell
            } else if (hintIndex < hints.length) {
              const rect = new PdfRect(x * cellW, y * cellH, cellW, cellH);
              const r = new PdfRect(
                rect.x + margin1,
                rect.y + margin1,
                cellW - 2 * margin1,
                cellH - 2 * margin1
              );
              const r1 = GCalc.centerRect(r);

              const url = urlQr(
                { a: 'h', id: `${id}-${label}`, i: hints[hintIndex], g: label },
                SITE_URL
              );
              p.drawQr(r1, PdfBrush.Black, url);
              p.drawRectangle(GUtil.cutPen, null, rect);
              hintIndex++;
            }
          }
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QR_Hra__Trail_Hint
// Multi-page; trail game with CZ/EN hints as next-item indicator
// ---------------------------------------------------------------------------
export class QR_Hra__Trail_Hint implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin1 = PdfUnit.FromMillimeters(5);
    const margin2 = PdfUnit.FromMillimeters(2);
    const countY = 5;
    const total = opts.list.length;
    const { origin, size } = calcOrigin(1, countY, margin1, PdfSize.A4);
    const id = genId(opts.data.random);
    const totalPages = Math.ceil(total / countY);

    const labels = opts.list.map((it) => it.text);
    const hintsCZ = HintUtil.getHintsUnique(labels, HintUtil.getHintsCZ.bind(HintUtil));
    const hintsEN = HintUtil.getHintsUnique(labels, HintUtil.getHintsEN.bind(HintUtil));

    for (let lang = 0; lang < 2; lang++) {
      // 0: CZ, 1: EN
      const hints = lang === 0 ? hintsCZ : hintsEN;
      for (let page = 0; page < totalPages; page++) {
        const p = pdf.addPage();
        const pageStartIndex = page * countY;
        const pageItems = opts.list.slice(pageStartIndex, Math.min(pageStartIndex + countY, total));

        const labelFont = p.adjustFontMulti(
          pageItems.map((it) => [it.label.getText() ?? '']),
          GUtil.baseFont,
          new PdfSize(size.width / 4, size.height)
        );

        for (let i = 0; i < pageItems.length; i++) {
          const item = pageItems[i];
          const index = pageStartIndex + i + 1;
          const nextItem = opts.list[index % total];
          const nextLabel = nextItem.text;
          const o1 = hints.get(nextLabel) ?? nextLabel;

          const rect = new PdfRect(origin.x, origin.y + i * size.height, size.width, size.height);
          const r = new PdfRect(rect.x, rect.y, (rect.width * 5) / 8, rect.height);

          const sz = Math.min(rect.width / 4, rect.height);
          const origX = rect.x + (rect.width - 4 * sz) / 2;
          const origY = rect.y + (rect.height - sz) / 2;
          const rs: PdfRect[] = [];
          for (let x = 0; x < 8; x++) {
            rs.push(
              new PdfRect(
                origX + (x * sz) / 2 + margin2,
                origY + margin2,
                sz - 2 * margin2,
                sz - 2 * margin2
              )
            );
          }

          const rs1 = new PdfRect(rs[5].x, rs[5].y, rs[3].width * 1.5, rs[3].height / 2);
          const rs2 = new PdfRect(rs1.x, rs1.bottom, rs1.width, rs1.height);

          const url = urlQr({ a: 'e', id, i: index, c: total, o: item.text, o1 }, SITE_URL);

          const r0 = new PdfRect(rs[0].x, rs[0].y, rs[0].width / 2, rs[0].height);
          const f0 = p.adjustFont(r0.height, [item.text], GUtil.baseFont);
          p.save();
          p.rotateAtTransform(270, new PdfPoint(r0.centerX, r0.centerY));
          p.drawString(item.text, f0, PdfBrush.Black, null, r0, ETextAlign.Center);
          p.restore();

          p.drawLabel(item.label as any, { rect: rs[1], font: labelFont });
          p.drawQr(rs[3], PdfBrush.Black, url);

          const f = p.adjustFont(rs2.width, [o1], GUtil.baseFont);
          p.drawArrowRight(rs1, BLUE);
          p.drawString(o1, f, PdfBrush.Black, null, rs2, ETextAlign.Center);

          p.drawRectangle(GUtil.cutPen, null, rect);
        }
      }
    }
  }
}
